class CustomFileUploader extends HTMLElement {

    constructor() {
        super();
        this.FilePond = null;
        this.ImagePreviewPlugin = null;
        this.FileTypeValidatorPlugin = null
        this.DownloadFilePlugin = null
        this.FileSizeValidationPlugin = null
        this.ALLOWED_SHOPIFY_MEDIA_TYPES = new Set([".png", ".jpg", '.jpeg', '.psd', '.bmp', '.gif', '.svg', '.heic', '.webp'])
        this.readyPromise = new Promise(resolve => {
            this.resolveReady = resolve;
        });
        this._fileValidationError = null;
    }


    connectedCallback() {
        this.render();

        const start = () => {
            this.initFilePond().catch((error) => {
                console.error('[CustomFileUploader] Failed to initialize FilePond', error);
            });
        };

        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', start);
        } else {
            start();
        }
    }

    waitForImageCompressor(maxWaitMs = 5000) {
        if (window.ImageCompressor) return Promise.resolve(true);

        return new Promise((resolve) => {
            const deadline = Date.now() + maxWaitMs;
            const tick = () => {
                if (window.ImageCompressor) {
                    resolve(true);
                    return;
                }
                if (Date.now() >= deadline) {
                    resolve(false);
                    return;
                }
                requestAnimationFrame(tick);
            };
            tick();
        });
    }

    _getUploadSizeErrorMessage(maxUploadSize, maxFileSize) {
        const sizeMb = Number(maxUploadSize || maxFileSize) || 5;
        return `File is too large. Maximum upload size is ${sizeMb} MB.`;
    }

    _getOutputSizeErrorMessage(maxFileSize, labelTemplate) {
        const formatted = (labelTemplate || '').trim();
        if (formatted && !formatted.includes('{')) return formatted;
        const sizeMb = Number(maxFileSize) || 5;
        return `Image could not be optimized to the required size (maximum ${sizeMb} MB).`;
    }

    _setFileValidationError(message, showFieldError = false) {
        this._fileValidationError = message;
        if (showFieldError) {
            this._showFieldError(message);
        }
    }

    _getFieldErrorId() {
        const errorIds = {
            'venue-images': 'venue_images-error',
            'sommelier-image': 'sommelier_image-error',
            'venue-wine-list': 'wine_list-error',
        };
        return errorIds[this.getAttribute('data-id')] || null;
    }

    _showFieldError(message) {
        const errorId = this._getFieldErrorId();
        if (!errorId || !message) return;

        this._fileValidationError = message;

        const errorSpan = document.getElementById(errorId);
        if (!errorSpan) return;

        errorSpan.textContent = message;
        errorSpan.style.display = 'block';
    }

    _clearFieldError() {
        const errorId = this._getFieldErrorId();
        this._fileValidationError = null;
        if (!errorId) return;

        const errorSpan = document.getElementById(errorId);
        if (!errorSpan) return;

        errorSpan.textContent = '';
        errorSpan.style.display = 'none';
    }

    hasFileValidationError() {
        if (this._fileValidationError) return true;

        const loadErrorStatus = this.FilePond?.FileStatus?.LOAD_ERROR ?? 8;
        return this.pond?.getFiles().some((fileItem) => fileItem.status === loadErrorStatus) ?? false;
    }

    getFileValidationError() {
        if (this._fileValidationError) return this._fileValidationError;

        const loadErrorStatus = this.FilePond?.FileStatus?.LOAD_ERROR ?? 8;
        const hasLoadError = this.pond?.getFiles().some((fileItem) => fileItem.status === loadErrorStatus);
        if (hasLoadError && this._uploadSizeErrorMessage) {
            return this._uploadSizeErrorMessage;
        }

        return this._uploadSizeErrorMessage
            || this._outputSizeErrorMessage
            || 'File is too large';
    }

    _getCompressionOptions(maxFileSizeBytes) {
        return {
            maxBytes: maxFileSizeBytes,
            maxDimension: 1920,
            quality: 0.82,
        };
    }

    _createBeforeAddFileHandler(maxFileSizeBytes, outputSizeExceededLabel) {
        return (item) => {
            const sourceFile = item.file;
            if (!sourceFile) return true;

            if (!window.ImageCompressor) return true;

            if (!window.ImageCompressor.isCompressibleImage(sourceFile)) {
                if (sourceFile.size > maxFileSizeBytes) {
                    this._showFieldError(outputSizeExceededLabel);
                    return false;
                }
                return true;
            }

            return window.ImageCompressor.compressImageIfNeeded(
                sourceFile,
                this._getCompressionOptions(maxFileSizeBytes)
            ).then((compressedFile) => {
                if (compressedFile.size > maxFileSizeBytes) {
                    this._showFieldError(outputSizeExceededLabel);
                    return false;
                }
                if (compressedFile !== sourceFile) {
                    item.setFile(compressedFile);
                }
                this._clearFieldError();
                return true;
            }).catch(() => {
                this._showFieldError(outputSizeExceededLabel);
                return false;
            });
        };
    }


    render() {
        const accept = this.getAttribute('accept') || '';
        const dataId = this.getAttribute('data-id') || `filepond-${Math.random().toString(36).substr(2, 9)}`;
        
        this.innerHTML = `
            <input type="file" 
                class="filepond-input"
                data-id="${dataId}"
                multiple
                accept="${accept}" 
            />
        `;
    }
     getFileExtension(input = '') {
        // Accept a File/Blob (with .name) or a string (filename/URL)
        let s = typeof input === 'string' ? input : (input && input.name) ? input.name : '';
        if (!s) return '';
      
        // Remove querystring and hash if present
        s = s.split(/[?#]/)[0];
      
        // Get last path segment
        const last = s.split('/').pop() || '';
      
        // Find last dot (ignore dotfiles like ".env")
        const dot = last.lastIndexOf('.');
        if (dot <= 0 || dot === last.length - 1) return '';
      
        return ('.' + last.slice(dot + 1)).toLowerCase();
    }
      

    async initFilePond() {
        const shouldCompress = this.getAttribute('data-compress') === 'true';
        if (shouldCompress) {
            await this.waitForImageCompressor();
        }

        this.FilePond = window.FilePond;
        this.ImagePreviewPlugin = window.FilePondPluginImagePreview;
        this.FileTypeValidatorPlugin = window.FilePondPluginFileValidateType;
        this.DownloadFilePlugin = window.FilePondPluginGetFile;
        this.FileSizeValidationPlugin = window.FilePondPluginFileValidateSize;

        const filePondplugins = [
            this.ImagePreviewPlugin, 
            this.FileTypeValidatorPlugin, 
            this.DownloadFilePlugin,
            this.FileSizeValidationPlugin
        ]; 
        const input = this.querySelector('.filepond-input');

        if ( input && this.FilePond && filePondplugins.every(p => p != null && p != undefined)) {
            // Pond plugins
            if (!window.__filepondPluginRegistered) {
                for (const plugin of filePondplugins) {
                    this.FilePond.registerPlugin(plugin)
                }
                window.__filepondPluginRegistered = true;
            }

            // Label properties
            const mainLabelText = this.getAttribute("label-idle") || null
            const mainLabelBrowseText = this.getAttribute("label-action") || null

            // console.log("Label====", mainLabelText)
            const labelIdle = mainLabelText && mainLabelBrowseText ? `${mainLabelText} <span class="filepond--label-action"> ${mainLabelBrowseText} </span>` : null

            // Get max file size in MB and convert to bytes
            const maxFileSize = this.getAttribute('max-file-size') || 5;
            const maxFileSizeBytes = Number(maxFileSize) * 1024 * 1024; // Convert MB to bytes
            const maxUploadSize = this.getAttribute('max-upload-size');
            const maxUploadSizeBytes = maxUploadSize ? Number(maxUploadSize) * 1024 * 1024 : null;
            const labelMaxSizeExceeded = this.getAttribute('label-max-size-exceeded');
            const compressionReady = shouldCompress && Boolean(window.ImageCompressor);
            const pondMaxFileSize = compressionReady
                ? (maxUploadSizeBytes || maxFileSizeBytes)
                : maxFileSizeBytes;
            const skipCompressibleFromPondSizeValidation = compressionReady && !maxUploadSizeBytes;
            const uploadSizeErrorMessage = this._getUploadSizeErrorMessage(
                maxUploadSize,
                maxFileSize
            );
            const outputSizeErrorMessage = this._getOutputSizeErrorMessage(
                maxFileSize,
                labelMaxSizeExceeded
            );
            this._uploadSizeErrorMessage = uploadSizeErrorMessage;
            this._outputSizeErrorMessage = outputSizeErrorMessage;

            // Pond configurations
            this.pond = this.FilePond.create(input, {
                inputId: input.getAttribute('data-id'),
                maxFiles: parseInt(this.getAttribute('max-files')) || null,
                acceptedFileTypes: this.getAttribute("accept") || null,
                maxFileSize: pondMaxFileSize,
                ...(skipCompressibleFromPondSizeValidation ? {
                    fileValidateSizeFilter: (file) => {
                        return !window.ImageCompressor.isCompressibleImage(file);
                    },
                } : {}),
                labelMaxFileSize: '',
                labelMaxFileSizeExceeded: uploadSizeErrorMessage,
                fileValidateTypeDetectType: (source, type) => {
                    return new Promise((resolve) => { 
                      if(type.startsWith('image/')){
                         const ext = this.getFileExtension(source && source.name ? source.name : source);
                            if (!ext || !this.ALLOWED_SHOPIFY_MEDIA_TYPES.has(ext)) {
                                resolve('invalid/type');
                                return;
                            }
                      }
                      resolve(type);
                    });
                  },
                  labelFileTypeNotAllowed: 'This file type is not allowed.',
                    fileValidateTypeLabelExpectedTypes: 'Please upload: {allButLastType} or {lastType}',
                onupdatefiles: () => {
                    const loadErrorStatus = this.FilePond?.FileStatus?.LOAD_ERROR ?? 8;
                    const hasLoadError = this.pond?.getFiles().some((fileItem) => fileItem.status === loadErrorStatus);
                    if (!hasLoadError) {
                        this._clearFieldError();
                    }
                    this.dispatchEvent(new Event('change', { bubbles: true }));
                },
                onwarning: (error) => {
                    if (error?.body === 'Max files') return;
                    this._setFileValidationError(uploadSizeErrorMessage);
                },
                onerror: () => {
                    this._setFileValidationError(uploadSizeErrorMessage);
                },
                ...(labelIdle != null) ? { labelIdle } : {},
                ...(compressionReady ? {
                    beforeAddFile: this._createBeforeAddFileHandler(
                        maxFileSizeBytes,
                        outputSizeErrorMessage
                    ),
                } : {}),
                files: this._parseDefaultFilesAttr(),
            });
            this.resolveReady?.(this.pond);
            
        } else {
            console.error('[CustomFileUploader] FilePond or plugin not loaded. Please include scripts before use.');
        }
    }

    getFiles() {
        return this.pond?.getFiles()?.map(fileItem => this._fileWithMetadata(fileItem)) ?? [];
    }

    getFileEntries() {
        return this.pond?.getFiles()?.map(fileItem => {
            const file = this._fileWithMetadata(fileItem);
            return {
                file,
                fileName: file?.name || '',
                shopifyId: file?.shopifyId,
                shopifyUrl: file?.shopifyUrl
            };
        }) ?? [];
    }

    _fileWithMetadata(fileItem) {
        const file = fileItem?.file;
        if (!file) return file;

        const shopifyId = file.shopifyId || fileItem.getMetadata?.('shopifyId');
        const shopifyUrl = file.shopifyUrl || fileItem.getMetadata?.('shopifyUrl');

        if (shopifyId) file.shopifyId = shopifyId;
        if (shopifyUrl) file.shopifyUrl = shopifyUrl;

        return file;
    }

    async waitUntilReady() {
        if (this.pond) return this.pond;
        return this.readyPromise;
    }

    async setFilesFromUrls(files = []) {
        const pond = await this.waitUntilReady();
        if (!pond) return;

        const fileEntries = Array.isArray(files) ? files : [files];
        const validFiles = fileEntries.filter(file => file?.url);
        pond.removeFiles();

        for (const fileEntry of validFiles) {
            try {
                const response = await fetch(fileEntry.url);
                const blob = await response.blob();
                const fileName = fileEntry.url.split('/').pop().split('?')[0];
                const file = new File([blob], fileName, { type: blob.type });
                if (fileEntry.id) file.shopifyId = fileEntry.id;
                file.shopifyUrl = fileEntry.url;
                const fileItem = await pond.addFile(file);
                fileItem?.setMetadata?.('shopifyId', fileEntry.id);
                fileItem?.setMetadata?.('shopifyUrl', fileEntry.url);
            } catch (error) {
                try {
                    const fileItem = await pond.addFile(fileEntry.url);
                    fileItem?.setMetadata?.('shopifyId', fileEntry.id);
                    fileItem?.setMetadata?.('shopifyUrl', fileEntry.url);
                } catch (fallbackError) {
                    console.error('[CustomFileUploader] Error loading file:', fallbackError);
                }
            }
        }
    }

    setDisabled(isDisabled) {
        const disabled = Boolean(isDisabled);
        this.toggleAttribute('data-disabled', disabled);
        this.style.pointerEvents = disabled ? 'none' : '';
        this.style.opacity = disabled ? '0.7' : '';

        const input = this.querySelector('.filepond-input');
        if (input) input.disabled = disabled;

        if (this.pond) {
            this.pond.setOptions({
                allowBrowse: !disabled,
                allowDrop: !disabled,
                allowPaste: !disabled,
                allowRemove: !disabled,
                allowReorder: !disabled,
            });
        }

        this.querySelectorAll('.filepond--action-remove-item').forEach(button => {
            button.disabled = disabled;
            button.style.display = disabled ? 'none' : '';
        });

        if (!disabled) {
            requestAnimationFrame(() => {
                this.pond?.setOptions({ allowRemove: true });
                this.querySelectorAll('.filepond--action-remove-item').forEach(button => {
                    button.disabled = false;
                    button.style.display = '';
                    button.style.pointerEvents = '';
                });
            });
        }
    }

    validFiles(){
        // Validate the field is not empty 
        return this.pond.getFiles().length > 0
    }

    /**
     *  Gets and parse defaults files using the 'default-files' attribute
     *  */ 
    _parseDefaultFilesAttr() {
        const attr = this.getAttribute('default-files');
        if (!attr) return [];
      
        try {
          const urls = JSON.parse(attr);
          return Array.isArray(urls)
            ? urls.map(url => ({
                source: url
              }))
            : [];
        } catch (err) {
          console.warn('Invalid default-files JSON', err);
          return [];
        }
      }

}

customElements.define('custom-file-uploader', CustomFileUploader);
